import { useState, useEffect } from "react";
import ConfigAPI from "../../services/ConfigAPI";

export default function StatusConfigSection() {
    const [statusConfigs, setStatusConfigs] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [formData, setFormData] = useState({
        displayName: '',
        searchTerms: [''],
        matchType: 'contains',
        color: '#667eea',
        priority: 0,
        comment: '',
        responseType: 'outros'
    });

    useEffect(() => {
        loadStatusConfigs();
    }, []);

    const loadStatusConfigs = async () => {
        try {
            setLoading(true);
            const response = await ConfigAPI.getStatusConfigs();
            if (response.success) {
                setStatusConfigs(response.data);
            }
        } catch (err) {
            console.error("Erro ao carregar status configs:", err);
            setError("Erro ao carregar configurações de status");
        } finally {
            setLoading(false);
        }
    };

    const handleAddSearchTerm = () => {
        setFormData(prev => ({
            ...prev,
            searchTerms: [...prev.searchTerms, '']
        }));
    };

    const handleRemoveSearchTerm = (index) => {
        setFormData(prev => ({
            ...prev,
            searchTerms: prev.searchTerms.filter((_, i) => i !== index)
        }));
    };

    const handleSearchTermChange = (index, value) => {
        setFormData(prev => ({
            ...prev,
            searchTerms: prev.searchTerms.map((term, i) => i === index ? value : term)
        }));
    };

    const handleEdit = (status) => {
        setEditingStatus(status);
        setFormData({
            displayName: status.displayName,
            searchTerms: [...status.searchTerms],
            matchType: status.matchType,
            color: status.color,
            priority: status.priority,
            comment: status.comment,
            responseType: status.responseType
        });
        setShowAddModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja excluir esta configuração?')) {
            return;
        }

        try {
            setLoading(true);
            const response = await ConfigAPI.deleteStatusConfig(id);
            
            if (response.success) {
                setSuccess("Status deletado com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadStatusConfigs();
            }
        } catch (err) {
            console.error("Erro ao deletar status:", err);
            setError(err.response?.data?.message || "Erro ao deletar status");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        const validSearchTerms = formData.searchTerms.filter(term => term.trim() !== '');
        
        if (!formData.displayName.trim()) {
            alert('O nome de exibição é obrigatório');
            return;
        }

        if (validSearchTerms.length === 0) {
            alert('Adicione pelo menos um termo de busca');
            return;
        }

        try {
            setLoading(true);
            
            const data = {
                displayName: formData.displayName,
                searchTerms: validSearchTerms,
                matchType: formData.matchType,
                color: formData.color,
                priority: formData.priority,
                comment: formData.comment,
                responseType: formData.responseType
            };

            let response;
            
            if (editingStatus) {
                response = await ConfigAPI.updateStatusConfig(editingStatus._id, data);
            } else {
                response = await ConfigAPI.addStatusConfig(data);
            }

            if (response.success) {
                setSuccess(editingStatus ? "Status atualizado com sucesso" : "Status criado com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                resetForm();
                setShowAddModal(false);
                await loadStatusConfigs();
            }
        } catch (err) {
            console.error("Erro ao salvar status:", err);
            setError(err.response?.data?.message || "Erro ao salvar status");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            displayName: '',
            searchTerms: [''],
            matchType: 'contains',
            color: '#667eea',
            priority: 0,
            comment: '',
            responseType: 'outros'
        });
        setEditingStatus(null);
    };

    const handleResetToDefaults = async () => {
        if (!window.confirm('Deseja restaurar as configurações padrão? Todas as alterações serão perdidas.')) {
            return;
        }

        try {
            setLoading(true);
            const response = await ConfigAPI.resetStatusConfigs();
            
            if (response.success) {
                setSuccess("Configurações restauradas com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadStatusConfigs();
            }
        } catch (err) {
            console.error("Erro ao restaurar padrões:", err);
            setError(err.response?.data?.message || "Erro ao restaurar padrões");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const getResponseTypeLabel = (type) => {
        const labels = {
            'entrega': '✅ Entrega',
            'devolucao': '↩️ Devolução',
            'redespacho': '📦 Redespacho',
            'outros': '🔵 Outros'
        };
        return labels[type] || type;
    };

    return (
        <div className="section-content">
            <h1>Configuração de Status</h1>
            <p className="card-description ">
                Configure como os status das AWBs serão identificados e categorizados pelo sistema
            </p>

            {success && (
                <div className="alert-success">
                    <span>✓ {success}</span>
                </div>
            )}

            {error && (
                <div style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontWeight: '500'
                }}>
                    {error}
                </div>
            )}

            <div className="config-card">
                <div className="card-header">
                    <h3>Status Configurados ({statusConfigs.length})</h3>
                    <div>
                        <button 
                            className="btn-add" 
                            onClick={() => {
                                resetForm();
                                setShowAddModal(true);
                            }}
                            disabled={loading}
                        >
                            + Novo Status
                        </button>
                        <button 
                            className="btn-secondary" 
                            onClick={handleResetToDefaults}
                            disabled={loading}
                        >
                            🔄 Restaurar Padrões
                        </button>
                    </div>
                </div>

                {loading && statusConfigs.length === 0 ? (
                    <div className="empty-state">
                        <p>Carregando...</p>
                    </div>
                ) : statusConfigs.length === 0 ? (
                    <div className="empty-state">
                        <p>Nenhum status configurado ainda</p>
                    </div>
                ) : (
                    <div className="columns-list">
                        {statusConfigs.map((status) => (
                            <div key={status._id} className="column-item status-item">
                                <div className="status-color-indicator" style={{ backgroundColor: status.color }}></div>
                                
                                <div className="column-main">
                                    <div className="column-header">
                                        <h4>{status.displayName}</h4>
                                        <div className="column-badges">
                                            <span className="badge priority">
                                                Prioridade: {status.priority}
                                            </span>
                                            <span className={`badge match-${status.matchType}`}>
                                                {status.matchType === 'exact' ? 'Texto Exato' : 'Contém'}
                                            </span>
                                            <span className="badge response-type">
                                                {getResponseTypeLabel(status.responseType)}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="column-details">
                                        <div className="detail-group">
                                            <span className="detail-label">Termos de busca:</span>
                                            <div className="search-terms">
                                                {status.searchTerms.map((term, idx) => (
                                                    <span key={idx} className="search-term">
                                                        "{term}"
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {status.comment && (
                                            <div className="detail-group">
                                                <span className="detail-label">Comentário:</span>
                                                <p className="status-comment">{status.comment}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="column-actions">
                                    <button 
                                        className="btn-icon" 
                                        onClick={() => handleEdit(status)}
                                        disabled={loading}
                                        title="Editar"
                                    >
                                        ✏️
                                    </button>
                                    <button 
                                        className="btn-icon btn-danger-icon" 
                                        onClick={() => handleDelete(status._id)}
                                        disabled={loading}
                                        title="Deletar"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                }}>
                    <div className="modal-content mapping-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingStatus ? 'Editar Status' : 'Novo Status'}</h2>

                        <div className="form-group">
                            <label>Nome de Exibição *</label>
                            <input
                                type="text"
                                value={formData.displayName}
                                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                                placeholder="Ex: Entrega Realizada, Em Devolução, etc."
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Termos de Busca *</label>
                            <p className="helper-text">
                                Adicione os termos que devem ser procurados no subject da AWB
                            </p>
                            {formData.searchTerms.map((term, index) => (
                                <div key={index} className="search-term-input">
                                    <input
                                        type="text"
                                        value={term}
                                        onChange={(e) => handleSearchTermChange(index, e.target.value)}
                                        placeholder='Ex: "entrega", "entregue", "delivered"'
                                        className="monospace"
                                        disabled={loading}
                                    />
                                    {formData.searchTerms.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn-remove"
                                            onClick={() => handleRemoveSearchTerm(index)}
                                            disabled={loading}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button 
                                type="button" 
                                className="btn-secondary small" 
                                onClick={handleAddSearchTerm}
                                disabled={loading}
                            >
                                + Adicionar Termo
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Tipo de Correspondência</label>
                            <select
                                value={formData.matchType}
                                onChange={(e) => setFormData({ ...formData, matchType: e.target.value })}
                                disabled={loading}
                            >
                                <option value="contains">Contém (busca parcial)</option>
                                <option value="exact">Texto Exato</option>
                            </select>
                            <p className="helper-text">
                                {formData.matchType === 'contains' 
                                    ? 'Encontra o termo em qualquer parte do texto' 
                                    : 'O texto deve ser exatamente igual ao termo'}
                            </p>
                        </div>

                        <div className="form-group">
                            <label>Tipo de Resposta</label>
                            <select
                                value={formData.responseType}
                                onChange={(e) => setFormData({ ...formData, responseType: e.target.value })}
                                disabled={loading}
                            >
                                <option value="entrega">Entrega</option>
                                <option value="devolucao">Devolução</option>
                                <option value="redespacho">Redespacho</option>
                                <option value="outros">Outros</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Cor do Status</label>
                            <div className="color-picker">
                                <input
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    disabled={loading}
                                />
                                <span className="color-preview-text">{formData.color}</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Prioridade</label>
                            <input
                                type="number"
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                                placeholder="0"
                                disabled={loading}
                            />
                            <p className="helper-text">
                                Maior prioridade = verificado primeiro. Use para casos específicos que devem ter preferência.
                            </p>
                        </div>

                        <div className="form-group">
                            <label>Comentário (opcional)</label>
                            <textarea
                                value={formData.comment}
                                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                                placeholder="Adicione uma descrição ou observação sobre este status"
                                rows={3}
                                disabled={loading}
                            />
                        </div>

                        <div className="modal-actions">
                            <button 
                                className="btn-secondary" 
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-save" 
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : editingStatus ? 'Atualizar' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}